'use client'

import React, { useEffect, useRef } from 'react'
import { Cog } from 'lucide-react'
import { useCartModalOpen } from '@/components/Cart/useCartModalOpen'

/**
 * Hans — the interactive smoking-robot that lives on top of the open chat panel.
 *
 * Locomotion (position FSM): sits on the panel, wanders back and forth along the
 * top edge, can be grabbed + dragged; on release he falls, gets up, walks to the
 * far right and climbs back onto the window.
 *
 * Expression (driven by `activity`) while he is perched:
 *   - idle    → resting, with an occasional wave + a greeting wave on open
 *   - busy    → thinks (review row) briefly, then works in place (running row)
 *   - success → celebrates with a jump when a reply lands (busy → idle)
 *   - error   → slumps (failed row) when a message fails
 *   - waiting → patient stance when awaiting user input/approval
 *
 * Driven by one requestAnimationFrame loop that mutates the DOM directly.
 */

// ---- Sprite sheet geometry ------------------------------------------------
const FW = 192
const FH = 208
const SCALE = 0.48
const DRAW_W = Math.round(FW * SCALE) // 92
// Exported so ChatPanel.tsx can reserve enough empty space above the
// mobile bottom sheet for Hans to perch in without his head clipping off
// the top of the viewport — see MOBILE_TOP_RESERVE there.
export const DRAW_H = Math.round(FH * SCALE) // 100
const SRC = '/sprites/hermes-sprites/pets/smoking-robot/spritesheet.webp'

// Rows (see metadata.json stateRows).
const ROW_IDLE = 0
const ROW_RUN_R = 1 // running-right
const ROW_RUN_L = 2 // running-left
const ROW_WAVE = 3 // waving / greeting
const ROW_JUMP = 4 // celebrate / spring jump (also used for climbing)
const ROW_FAILED = 5 // sad / broken / slumped
const ROW_WAITING = 6 // waiting for input
const ROW_WORK = 7 // in-place work / processing
const ROW_REVIEW = 8 // inspection / thinking

// Populated frames per row (trailing sheet cells are blank → flicker if drawn).
const FRAME_COUNTS: Record<number, number> = {
  [ROW_IDLE]: 6,
  [ROW_RUN_R]: 8,
  [ROW_RUN_L]: 8,
  [ROW_WAVE]: 4,
  [ROW_JUMP]: 5,
  [ROW_FAILED]: 8,
  [ROW_WAITING]: 6,
  [ROW_WORK]: 6,
  [ROW_REVIEW]: 6
}
const DEFAULT_FRAMES = 6

// Full-loop duration per row (ms).
const LOOP_MS: Record<number, number> = {
  [ROW_IDLE]: 1100,
  [ROW_RUN_R]: 820,
  [ROW_RUN_L]: 820,
  [ROW_WAVE]: 700,
  [ROW_JUMP]: 700,
  [ROW_FAILED]: 1400,
  [ROW_WAITING]: 1400,
  [ROW_WORK]: 520,
  [ROW_REVIEW]: 1200
}

// ---- Motion tuning --------------------------------------------------------
const GRAVITY = 2600 // px/s^2
const CLIMB_SPEED = 135 // px/s upward
const RETURN_SPEED = 78 // px/s walking back to perch
const MARGIN = 8

// ---- Timing ---------------------------------------------------------------
const SIT_MIN_MS = 30000 // sit at least ~30s before wandering again
const SIT_MAX_MS = 48000
const GREET_MS = 1100 // greeting wave on open
const THINK_PHASE_MS = 1300 // "review/thinking" before switching to "working"
const CELEBRATE_MS = 1600 // jump when a reply lands
const FAILED_MS = 2400 // slump on error
const WAVE_DUR_MS = 1400 // two wave cycles
const WAVE_MIN_MS = 12000 // idle wave cadence
const WAVE_MAX_MS = 22000

const rand = (min: number, max: number) => min + Math.random() * (max - min)

export type HansActivity = 'idle' | 'busy' | 'waiting' | 'error'

type Mode =
  | 'sitting'
  | 'walking'
  | 'pausing'
  | 'returning'
  | 'dragging'
  | 'falling'
  | 'gettingUp'
  | 'walkingRight'
  | 'climbing'

type Expr = 'idle' | 'wave' | 'waiting' | 'thinking' | 'working' | 'celebrate' | 'failed'

interface Geom {
  perchX: number
  perchY: number
  topY: number
  xMin: number
  xMax: number
  floorY: number
  farRightX: number
}

interface ChatRobotProps {
  panelRef: React.RefObject<HTMLElement | null>
  /** True on the mobile bottom sheet (ChatPanel.tsx MOBILE_TOP_RESERVE
   * leaves real empty space above it, same as desktop, so geom() needs no
   * special case there) — only used here to lift Hans above the site's
   * sticky header (z-50) so he isn't hidden behind it while perched in
   * that reserved gap. */
  isMobile?: boolean
  /** Current assistant state — drives Hans's expression while he is perched. */
  activity?: HansActivity
}

const exprRow = (e: Expr): number => {
  switch (e) {
    case 'wave':
      return ROW_WAVE
    case 'waiting':
      return ROW_WAITING
    case 'thinking':
      return ROW_REVIEW
    case 'working':
      return ROW_WORK
    case 'celebrate':
      return ROW_JUMP
    case 'failed':
      return ROW_FAILED
    default:
      return ROW_IDLE
  }
}

const baseExpr = (a: HansActivity): Expr => {
  switch (a) {
    case 'busy':
      return 'thinking'
    case 'waiting':
      return 'waiting'
    case 'error':
      return 'failed'
    default:
      return 'idle'
  }
}

export const ChatRobot: React.FC<ChatRobotProps> = ({
  panelRef,
  isMobile = false,
  activity = 'idle',
}) => {
  const wrapRef = useRef<HTMLDivElement>(null)
  const spriteRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  // Mirror the panel's z-index lift so Hans stays above the cart
  // Sheet overlay when the panel does. See docs/chatbot-sprite/19.
  const cartModalOpen = useCartModalOpen()

  // Latest activity, readable inside the rAF loop without re-initialising it.
  const activityRef = useRef<HansActivity>(activity)
  useEffect(() => {
    activityRef.current = activity
  }, [activity])

  useEffect(() => {
    const wrap = wrapRef.current
    const sprite = spriteRef.current
    const canvas = canvasRef.current
    if (!wrap || !sprite || !canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const img = new Image()
    img.src = SRC

    let raf = 0
    let last = performance.now()
    let ready = false

    let mode: Mode = 'sitting'
    const pos = { x: 0, y: 0 }
    let dir: 1 | -1 = 1
    let vy = 0
    let targetX = 0
    let legsLeft = 0
    let speed = 60

    // Expression sub-state (only meaningful while perched / sitting).
    let expr: Expr = 'idle'
    let exprTimer = 0 // countdown for time-limited expressions
    let wanderTimer = 0 // countdown to next wander (idle only)
    let waveTimer = 0 // countdown to next idle wave
    let prevActivity: HansActivity = 'idle'
    let greeted = false

    let row = ROW_IDLE
    let animRow = -1
    let frame = 0
    let frameClock = 0

    const drag = { active: false, dx: 0, dy: 0 }

    const geom = (): Geom | null => {
      const el = panelRef.current
      if (!el) return null
      const r = el.getBoundingClientRect()
      // Perch above the panel's own top edge, on desktop AND mobile alike.
      // On mobile ChatPanel.tsx reserves real empty space above the sheet
      // (MOBILE_TOP_RESERVE) specifically so this works unmodified — see
      // the comment there for why anchoring to the header bar instead
      // (an earlier attempt) clipped his head off the top of the screen.
      return {
        perchX: r.right - DRAW_W - 24,
        perchY: r.top - DRAW_H + 8,
        topY: r.top - DRAW_H + 8,
        xMin: r.left + MARGIN,
        xMax: r.right - DRAW_W - MARGIN,
        floorY: window.innerHeight - DRAW_H - MARGIN,
        farRightX: window.innerWidth - DRAW_W - MARGIN
      }
    }

    const approach = (from: number, to: number, s: number): number =>
      Math.abs(to - from) <= s ? to : from + Math.sign(to - from) * s

    const startWander = (g: Geom) => {
      legsLeft = Math.round(rand(2, 5))
      speed = rand(42, 84)
      targetX = rand(g.xMin, g.xMax)
      dir = targetX >= pos.x ? 1 : -1
      mode = 'walking'
    }

    const walkRow = () => (dir >= 0 ? ROW_RUN_R : ROW_RUN_L)

    const step = (now: number) => {
      const dtMs = Math.min(now - last, 48)
      last = now
      const dt = dtMs / 1000
      const g = geom()
      const act = activityRef.current

      if (g) {
        if (!ready) {
          pos.x = g.perchX
          pos.y = g.perchY
          wanderTimer = rand(8000, 14000)
          waveTimer = rand(WAVE_MIN_MS, WAVE_MAX_MS)
          ready = true
          wrap.style.opacity = '1'
        }

        // If the assistant starts working while Hans is off wandering, hurry back.
        if (act !== 'idle' && (mode === 'walking' || mode === 'pausing')) {
          targetX = g.perchX
          speed = RETURN_SPEED
          mode = 'returning'
        }

        switch (mode) {
          case 'sitting': {
            pos.x = g.perchX
            pos.y = g.perchY

            // Greeting wave the first time he settles after opening.
            if (!greeted) {
              greeted = true
              expr = 'wave'
              exprTimer = GREET_MS
            }

            // One-shot reactions on activity transitions.
            if (prevActivity === 'busy' && act === 'idle') {
              expr = 'celebrate'
              exprTimer = CELEBRATE_MS
            } else if (act === 'error' && prevActivity !== 'error') {
              expr = 'failed'
              exprTimer = FAILED_MS
            }
            prevActivity = act

            // Time-limited expressions (greet/wave, celebrate, failed).
            if (expr === 'wave' || expr === 'celebrate' || expr === 'failed') {
              exprTimer -= dtMs
              if (exprTimer <= 0) {
                expr = baseExpr(act)
                if (expr === 'thinking') exprTimer = THINK_PHASE_MS
              }
              row = exprRow(expr)
              break
            }

            // Busy: think for a beat, then work in place.
            if (act === 'busy') {
              if (expr !== 'thinking' && expr !== 'working') {
                expr = 'thinking'
                exprTimer = THINK_PHASE_MS
              }
              if (expr === 'thinking') {
                exprTimer -= dtMs
                if (exprTimer <= 0) expr = 'working'
              }
              row = exprRow(expr)
              break
            }

            if (act === 'waiting') {
              expr = 'waiting'
              row = ROW_WAITING
              break
            }
            if (act === 'error') {
              expr = 'failed'
              row = ROW_FAILED
              break
            }

            // Idle: occasional wave, and eventually wander off.
            expr = 'idle'
            wanderTimer -= dtMs
            if (wanderTimer <= 0) {
              startWander(g)
              break
            }
            waveTimer -= dtMs
            if (waveTimer <= 0) {
              expr = 'wave'
              exprTimer = WAVE_DUR_MS
              waveTimer = rand(WAVE_MIN_MS, WAVE_MAX_MS)
              row = ROW_WAVE
              break
            }
            row = ROW_IDLE
            break
          }
          case 'walking': {
            pos.y = g.topY
            const tx = Math.min(Math.max(targetX, g.xMin), g.xMax)
            dir = tx >= pos.x ? 1 : -1
            row = walkRow()
            pos.x = approach(pos.x, tx, speed * dt)
            if (pos.x === tx) {
              legsLeft -= 1
              if (legsLeft > 0) {
                exprTimer = rand(180, 950)
                mode = 'pausing'
              } else {
                targetX = g.perchX
                speed = RETURN_SPEED
                mode = 'returning'
              }
            }
            break
          }
          case 'pausing': {
            pos.y = g.topY
            row = ROW_IDLE
            exprTimer -= dtMs
            if (exprTimer <= 0) {
              targetX = rand(g.xMin, g.xMax)
              speed = rand(42, 84)
              dir = targetX >= pos.x ? 1 : -1
              mode = 'walking'
            }
            break
          }
          case 'returning': {
            pos.y = g.topY
            dir = g.perchX >= pos.x ? 1 : -1
            row = walkRow()
            pos.x = approach(pos.x, g.perchX, speed * dt)
            if (pos.x === g.perchX) {
              wanderTimer = rand(SIT_MIN_MS, SIT_MAX_MS)
              mode = 'sitting'
            }
            break
          }
          case 'dragging': {
            row = ROW_IDLE
            break
          }
          case 'falling': {
            row = ROW_IDLE
            vy += GRAVITY * dt
            pos.y += vy * dt
            if (pos.y >= g.floorY) {
              pos.y = g.floorY
              vy = 0
              exprTimer = 320
              mode = 'gettingUp'
            }
            break
          }
          case 'gettingUp': {
            row = ROW_IDLE
            pos.y = g.floorY
            exprTimer -= dtMs
            if (exprTimer <= 0) {
              targetX = g.farRightX
              speed = rand(70, 95)
              dir = 1
              mode = 'walkingRight'
            }
            break
          }
          case 'walkingRight': {
            pos.y = g.floorY
            dir = 1
            row = ROW_RUN_R
            pos.x = approach(pos.x, g.farRightX, speed * dt)
            if (pos.x === g.farRightX) {
              mode = 'climbing'
            }
            break
          }
          case 'climbing': {
            row = ROW_JUMP
            pos.x = approach(pos.x, g.perchX, RETURN_SPEED * dt)
            pos.y = approach(pos.y, g.perchY, CLIMB_SPEED * dt)
            if (pos.y === g.perchY && Math.abs(pos.x - g.perchX) < 2) {
              pos.x = g.perchX
              wanderTimer = rand(SIT_MIN_MS, SIT_MAX_MS)
              prevActivity = act
              mode = 'sitting'
            }
            break
          }
        }
      }

      // ---- Render ----
      if (row !== animRow) {
        animRow = row
        frame = 0
        frameClock = 0
      }
      const frameCount = FRAME_COUNTS[row] ?? DEFAULT_FRAMES
      frameClock += dtMs
      const stepMs = (LOOP_MS[row] ?? 1100) / frameCount
      if (frameClock >= stepMs) {
        frame = (frame + 1) % frameCount
        frameClock = 0
      }
      if (img.complete && img.naturalWidth > 0) {
        ctx.clearRect(0, 0, DRAW_W, DRAW_H)
        ctx.imageSmoothingEnabled = false
        ctx.drawImage(img, frame * FW, row * FH, FW, FH, 0, 0, DRAW_W, DRAW_H)
      }
      wrap.style.left = `${Math.round(pos.x)}px`
      wrap.style.top = `${Math.round(pos.y)}px`

      raf = requestAnimationFrame(step)
    }

    // ---- Pointer (drag) handling ----
    const onPointerDown = (e: PointerEvent) => {
      drag.active = true
      drag.dx = e.clientX - pos.x
      drag.dy = e.clientY - pos.y
      mode = 'dragging'
      row = ROW_IDLE
      wrap.setPointerCapture(e.pointerId)
      wrap.style.cursor = 'grabbing'
    }
    const onPointerMove = (e: PointerEvent) => {
      if (!drag.active) return
      pos.x = Math.min(Math.max(0, e.clientX - drag.dx), window.innerWidth - DRAW_W)
      pos.y = Math.min(Math.max(0, e.clientY - drag.dy), window.innerHeight - DRAW_H)
    }
    const onPointerUp = (e: PointerEvent) => {
      if (!drag.active) return
      drag.active = false
      vy = 0
      mode = 'falling'
      wrap.style.cursor = 'grab'
      try {
        wrap.releasePointerCapture(e.pointerId)
      } catch {
        /* already released */
      }
    }

    wrap.addEventListener('pointerdown', onPointerDown)
    wrap.addEventListener('pointermove', onPointerMove)
    wrap.addEventListener('pointerup', onPointerUp)

    raf = requestAnimationFrame(step)

    return () => {
      cancelAnimationFrame(raf)
      wrap.removeEventListener('pointerdown', onPointerDown)
      wrap.removeEventListener('pointermove', onPointerMove)
      wrap.removeEventListener('pointerup', onPointerUp)
    }
  }, [panelRef])

  return (
    <div
      ref={wrapRef}
      aria-label="Hans"
      title="Hans"
      role="img"
      style={{
        position: 'fixed',
        left: 0,
        top: 0,
        // z-42: perches just above the chat panel (z-41) and the chat
        // trigger (z-40). Must stay below the Radix Sheet / Dialog
        // / Search stack at z-50 so the cart modal can cover Hans
        // when the user opens the cart from the chat's CTA card
        // (the bug fixed in docs/chatbot-sprite/19). EXCEPTION 1: when
        // the cart is open we raise to z-52 in lock-step with the panel
        // (z-51) so the customer can still see and click Hans if he
        // perches on the left-of-cart panel. EXCEPTION 2: on the mobile
        // bottom sheet he perches in the reserved gap above the panel,
        // which is also where the site's sticky header (z-50) lives —
        // z-51 keeps him visibly standing in front of it there.
        zIndex: isMobile ? 51 : cartModalOpen ? 52 : 42,
        width: DRAW_W,
        height: DRAW_H,
        lineHeight: 0,
        opacity: 0,
        cursor: 'grab',
        touchAction: 'none',
        userSelect: 'none',
        filter: 'drop-shadow(0 6px 5px rgba(0, 0, 0, 0.28))'
      }}
    >
      {/* Thinking bubble while the assistant works — follows Hans around. */}
      {activity === 'busy' && (
        <div
          style={{
            position: 'absolute',
            bottom: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            marginBottom: '4px',
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
            padding: '4px 8px',
            borderRadius: '11px',
            background: 'rgba(15, 23, 42, 0.94)',
            border: '1px solid rgba(255, 140, 0, 0.4)',
            boxShadow: '0 4px 14px rgba(0, 0, 0, 0.4)',
            pointerEvents: 'none',
            whiteSpace: 'nowrap'
          }}
        >
          <Cog size={13} color="#ff8c1a" strokeWidth={2.4} className="hans-gear" />
          <span className="hans-dots" aria-hidden>
            <i />
            <i />
            <i />
          </span>
          <span
            aria-hidden
            style={{
              position: 'absolute',
              top: '100%',
              left: '50%',
              width: 0,
              height: 0,
              transform: 'translateX(-50%)',
              borderLeft: '4px solid transparent',
              borderRight: '4px solid transparent',
              borderTop: '5px solid rgba(15, 23, 42, 0.94)'
            }}
          />
        </div>
      )}

      <div ref={spriteRef} style={{ lineHeight: 0, transformOrigin: 'center bottom' }}>
        <canvas ref={canvasRef} width={DRAW_W} height={DRAW_H} style={{ width: DRAW_W, height: DRAW_H }} />
      </div>

      <style>{`
        @keyframes hansGearSpin { to { transform: rotate(360deg); } }
        @keyframes hansDotPulse { 0%, 80%, 100% { opacity: 0.25; transform: translateY(0); } 40% { opacity: 1; transform: translateY(-1px); } }
        .hans-gear { animation: hansGearSpin 2.4s linear infinite; will-change: transform; }
        .hans-dots { display: inline-flex; gap: 3px; }
        .hans-dots i { width: 4px; height: 4px; border-radius: 50%; background: #34d399; display: inline-block; animation: hansDotPulse 1.1s ease-in-out infinite; will-change: opacity, transform; }
        .hans-dots i:nth-child(2) { animation-delay: 0.18s; }
        .hans-dots i:nth-child(3) { animation-delay: 0.36s; }
      `}</style>
    </div>
  )
}

export default ChatRobot
